import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../../i18n/I18nContext";
import { useCart } from "../../context/CartContext";
import { api, apiError } from "../../lib/api";
import { toast } from "sonner";
import { Trash2, ShoppingBag } from "lucide-react";

export default function Cart() {
  const { t, localize, fmtSAR, isAr } = useI18n();
  const { items, setQty, remove, subtotal, branchId, clear } = useCart();
  const [info, setInfo] = useState({ customer_name: "", customer_phone: "", fulfilment: "pickup", address: "" });
  const [busy, setBusy] = useState(false);
  const [order, setOrder] = useState(null);

  const checkout = async () => {
    if (!info.customer_name || !info.customer_phone) { toast.error(t("common.required")); return; }
    setBusy(true);
    try {
      const branches = (await api.get("/branches")).data;
      const { data } = await api.post("/orders", {
        branch_id: branchId || branches[0]?.id,
        items: items.map((i) => ({ product_id: i.product_id, qty: i.qty })),
        fulfilment: info.fulfilment, address: info.address,
        customer_name: info.customer_name, customer_phone: info.customer_phone,
      });
      setOrder(data); clear(); toast.success(isAr ? "تم إنشاء الطلب" : "Order created");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (order) return (
    <div className="mx-auto max-w-md px-4 py-20 text-center">
      <ShoppingBag className="mx-auto h-14 w-14 text-green-500" />
      <h1 className="mt-4 text-2xl font-bold">{isAr ? "تم استلام طلبك" : "Order received"}</h1>
      <p className="mt-2 text-slate-500">{fmtSAR(order.total)} — {order.fulfilment === "pickup" ? (isAr ? "استلام من الفرع" : "Branch pickup") : (isAr ? "توصيل" : "Delivery")}</p>
      <Link to="/shop" className="mt-6 inline-block rounded-full bg-primary px-6 py-3 font-semibold text-white">{t("nav.shop")}</Link>
    </div>
  );

  if (items.length === 0) return (
    <div className="mx-auto max-w-md px-4 py-24 text-center">
      <ShoppingBag className="mx-auto h-14 w-14 text-slate-300" />
      <p className="mt-4 text-slate-500">{isAr ? "سلتك فارغة" : "Your cart is empty"}</p>
      <Link to="/shop" className="mt-6 inline-block rounded-full bg-primary px-6 py-3 font-semibold text-white">{t("nav.shop")}</Link>
    </div>
  );

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-8">
      <h1 className="text-3xl font-bold text-slate-900">{t("nav.cart")}</h1>
      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="space-y-3 lg:col-span-2">
          {items.map((i) => (
            <div key={i.product_id} className="flex items-center gap-4 rounded-xl border border-border bg-white p-3">
              <img src={i.image} alt="" className="h-16 w-16 rounded-lg object-cover" />
              <div className="flex-1"><div className="font-semibold text-slate-900">{localize(i, "name")}</div><div className="text-sm text-primary">{fmtSAR(i.price)}</div></div>
              <div className="flex items-center rounded-full border border-border">
                <button onClick={() => setQty(i.product_id, i.qty - 1)} className="px-3 py-1">−</button>
                <span className="w-8 text-center text-sm">{i.qty}</span>
                <button onClick={() => setQty(i.product_id, i.qty + 1)} className="px-3 py-1">+</button>
              </div>
              <button onClick={() => remove(i.product_id)} data-testid={`cart-remove-${i.product_id}`} className="text-[hsl(var(--brand-red))]"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <div className="rounded-2xl border border-border bg-white p-5">
          <h3 className="font-bold text-slate-900">{t("nav.cart")}</h3>
          <div className="mt-4 flex justify-between text-sm"><span>{t("common.subtotal")}</span><span className="font-semibold">{fmtSAR(subtotal)}</span></div>
          <div className="mt-4 space-y-3">
            <input placeholder={t("common.name")} value={info.customer_name} onChange={(e) => setInfo({ ...info, customer_name: e.target.value })} data-testid="cart-name" className="w-full rounded-lg border border-border px-3 py-2 text-sm" />
            <input placeholder={t("common.phone")} dir="ltr" value={info.customer_phone} onChange={(e) => setInfo({ ...info, customer_phone: e.target.value })} data-testid="cart-phone" className="w-full rounded-lg border border-border px-3 py-2 text-sm" />
            <select value={info.fulfilment} onChange={(e) => setInfo({ ...info, fulfilment: e.target.value })} data-testid="cart-fulfilment" className="w-full rounded-lg border border-border px-3 py-2 text-sm">
              <option value="pickup">{isAr ? "استلام من الفرع" : "Branch pickup"}</option>
              <option value="delivery">{isAr ? "توصيل (+25 ر.س)" : "Delivery (+25 SAR)"}</option>
            </select>
            {info.fulfilment === "delivery" && <input placeholder={t("common.address")} value={info.address} onChange={(e) => setInfo({ ...info, address: e.target.value })} className="w-full rounded-lg border border-border px-3 py-2 text-sm" />}
          </div>
          <button onClick={checkout} disabled={busy} data-testid="cart-checkout-btn" className="mt-4 w-full rounded-full bg-primary py-3 font-semibold text-white disabled:opacity-50 hover:bg-primary/90">{busy ? t("common.loading") : (isAr ? "إتمام الطلب" : "Checkout")}</button>
          <p className="mt-2 text-center text-xs text-amber-600">{isAr ? "الضريبة تُحسب عند الدفع" : "VAT calculated at checkout"}</p>
        </div>
      </div>
    </div>
  );
}
